import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useState } from "react";
import { fetchAssignedProsecutor } from "services/advocateComplaintService";
import { editComplaint } from "services/advocateComplaintService";
import { AssignedProsecutor, Prosecutor } from "types";
import { LoadingIcon } from "utils/constant";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: any;
  onComplaintsRefresh: any;
}

export default function UnderConstractionReaasignProsecutorModal({
  isOpen,
  setIsOpen,
  complaintData,
  onComplaintsRefresh,
}: DecisionModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [prosecutorsData, setProsecutorsData] = useState<AssignedProsecutor[]>(
    [],
  );
  const [formState, setFormState] = useState({
    assigned_prosecutor_id: "",
    assigned_prosecutor: null as Prosecutor | null,
  });

  useEffect(() => {
    const loadProsecutors = async () => {
      try {
        const data = await fetchAssignedProsecutor(
          user?.admin_unit,
          user?.org_unit,
          user?.access,
        );

        setProsecutorsData(
          data?.filter((item) =>
            (item.roles as string[]).includes("AdvocateDisciplineProsecutor"),
          ),
        );
      } catch (err) {
        // showToast("Failed to load prosecutors", "error");
      } finally {
        setLoading(false);
      }
    };
    loadProsecutors();
  }, [user, showToast]);

  const filtered = prosecutorsData?.filter((p) =>
    `${p?.user?.first_name} ${p?.user?.last_name}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (prosecutor: Prosecutor, name: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: prosecutor,
    }));
    setQuery("");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formState.assigned_prosecutor_id) {
      showToast(t("selectProsecutor"), "error");
      return;
    }

    setLoading(true);

    try {
      await editComplaint({
        ...complaintData,
        user_id_assigned_prosecutor: formState.assigned_prosecutor_id,
        assigned_prosecutor:
          `${formState?.assigned_prosecutor?.first_name || ""} ${
            formState?.assigned_prosecutor?.last_name || ""
          }` || "",
      });

      onComplaintsRefresh();
      setIsOpen(false);
      showToast(t("prosecutorReassignedSuccessfully"), "success");
      setFormState({
        assigned_prosecutor_id: "",
        assigned_prosecutor: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("failedToReassignProsecutor"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("reassignProsecutor")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          {/* Current Prosecutor Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              {t("currentAssignment")}
            </h3>
            {complaintData?.assigned_prosecutor ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                  <span className="text-sm font-bold text-yellow-500">
                    {complaintData.assigned_prosecutor
                      .split(" ")
                      .map((n: string) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {complaintData.assigned_prosecutor}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("currentlyAssignedProsecutor")}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t("noProsecutorAssigned")}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("pleaseAssignProsecutor")}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* New Prosecutor Selection */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-gray-700">
                {t("assignNewProsecutor")}
              </label>
              <span className="text-xs text-gray-500 bg-red-50 px-2 py-1 rounded">
                {t("required")}
              </span>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <div className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder={t("searchProsecutorsPlaceholder")}
                    className="w-full outline-none text-sm bg-transparent"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                {query && filtered && filtered.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filtered.map((p) => (
                      <div
                        key={p.user.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => {
                          handleSelect(p.user, "assigned_prosecutor");
                          setFormState((prev) => ({
                            ...prev,
                            assigned_prosecutor_id: p.user.id ?? "",
                          }));
                        }}
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                          <span className="text-xs font-bold text-yellow-500">
                            {p.user.first_name?.[0]}
                            {p.user.last_name?.[0]}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {p.user.first_name} {p.user.last_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {t("advocateDisciplineProsecutor")}
                          </p>
                        </div>
                        {formState.assigned_prosecutor_id === p.user.id && (
                          <svg
                            className="w-5 h-5 text-green-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  {t("selectFromList")}
                </label>
                <select
                  required
                  className="w-full border border-gray-300 bg-white rounded-lg p-3 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500"
                  name="assigned_prosecutor_id"
                  value={formState.assigned_prosecutor_id}
                  onChange={(e) => {
                    handleChange(e);
                    const selected = filtered?.find(
                      (p) => p.user.id === e.target.value,
                    );
                    if (selected && selected.user) {
                      handleSelect(selected.user, "assigned_prosecutor");
                    }
                  }}
                >
                  <option value="">{t("chooseProsecutor")}</option>
                  {filtered?.map((p) => (
                    <option key={p.user.id} value={p.user.id}>
                      {p.user.first_name} {p.user.last_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Selected Prosecutor Preview */}
              {formState.assigned_prosecutor && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-red-800">
                      {t("selectedProsecutor")}
                    </span>
                    <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                      {t("newAssignment")}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center">
                      <span className="text-sm font-bold text-white">
                        {formState.assigned_prosecutor.first_name?.[0]}
                        {formState.assigned_prosecutor.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formState.assigned_prosecutor.first_name}{" "}
                        {formState.assigned_prosecutor.last_name}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {t("willReplace")}{" "}
                        {complaintData?.assigned_prosecutor ||
                          t("theCurrentAssignment")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="sticky bottom-0 pt-4 bg-white border-t border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1">
              <p className="text-xs text-gray-600">
                <span className="font-medium">{t("note")}:</span>{" "}
                {t("reassignProsecutorNote")}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || !formState.assigned_prosecutor_id}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  {LoadingIcon}
                  <span>{t("reassigning")}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                  <span>{t("reassignProsecutor")}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </form>
    </SideModal>
  );
}
